<script setup lang="ts">
import { computed, onBeforeUnmount, shallowRef, watch } from 'vue';
import { RouterLink } from 'vue-router';
import {
  BookMarked,
  CirclePlay,
  ClipboardList,
  ExternalLink,
  RotateCcw,
  X
} from 'lucide-vue-next';
import UiButton from '@krafta/ui-kit/components/UiButton.vue';
import UiCallout from '@krafta/ui-kit/components/UiCallout.vue';
import UiCard from '@krafta/ui-kit/components/UiCard.vue';
import UiIconButton from '@krafta/ui-kit/components/UiIconButton.vue';
import UiSkeleton from '@krafta/ui-kit/components/UiSkeleton.vue';
import { readApiErrorMessage } from '@shared/api/httpClient';
import { useAuthStore } from '@app/stores/auth';
import { fetchHelpPage } from '../api/helpApi';
import type { HelpBlock, HelpPage, HelpTourCatalogItem } from '../types';

type HelpStepsBlock = Extract<HelpBlock, { type: 'steps' }>;
type HelpListBlock = Extract<HelpBlock, { type: 'list' }>;
type HelpTextListItem = {
  key: string;
  text: string;
  order: number;
};
const loadingSkeletonRows = Array.from({ length: 5 }, (_, index) => ({
  key: `help-drawer-skeleton:${index + 1}`
}));

const props = withDefaults(
  defineProps<{
    helpKey?: string | null;
    fallbackReason?: string | null;
    tour?: HelpTourCatalogItem | null;
    tourCompleted?: boolean;
    tours?: HelpTourCatalogItem[];
    completedTourKeys?: string[];
  }>(),
  {
    helpKey: null,
    fallbackReason: null,
    tour: null,
    tourCompleted: false,
    tours: () => [],
    completedTourKeys: () => []
  }
);

const emit = defineEmits<{
  'start-tour': [tourKey: string];
}>();

const open = defineModel<boolean>({ required: true });
const auth = useAuthStore();
const page = shallowRef<HelpPage | null>(null);
const loading = shallowRef(false);
const error = shallowRef<string | null>(null);
const fallbackTitle = 'Справка не подключена';
const fallbackMessage = 'Помощь для этого экрана ещё не добавлена. Откройте общий справочный центр или обратитесь к администратору.';
let pageRequestId = 0;
const visibleTours = computed(() => (props.tours.length ? props.tours : props.tour ? [props.tour] : []));
const completedTourKeySet = computed(() => new Set(props.completedTourKeys));

const primarySteps = computed<HelpStepsBlock | null>(
  () => page.value?.body.find((block): block is HelpStepsBlock => block.type === 'steps') ?? null
);
const primaryList = computed<HelpListBlock | null>(
  () => page.value?.body.find((block): block is HelpListBlock => block.type === 'list') ?? null
);
const keyFields = computed(() => page.value?.fields?.slice(0, 4) ?? []);
const normalizeTextListKey = (value: string) =>
  value
    .trim()
    .toLocaleLowerCase('ru-RU')
    .replace(/\s+/g, '-')
    .replace(/[^\p{L}\p{N}_-]+/gu, '')
    .replace(/-+/g, '-') || 'empty';

const createTextListItems = (prefix: string, items: string[] = []): HelpTextListItem[] => {
  const occurrences = new Map<string, number>();

  return items.map((text, index) => {
    const normalized = normalizeTextListKey(text);
    const occurrence = occurrences.get(normalized) ?? 0;
    occurrences.set(normalized, occurrence + 1);

    return {
      key: `${prefix}:${normalized}:${occurrence}`,
      text,
      order: index + 1
    };
  });
};
const primaryStepItems = computed(() =>
  createTextListItems('primary-step', primarySteps.value?.items)
);
const primaryListItems = computed(() =>
  createTextListItems('primary-list', primaryList.value?.items)
);

const close = () => {
  open.value = false;
};

const isTourCompleted = (tourKey: string) =>
  completedTourKeySet.value.has(tourKey) || Boolean(props.tourCompleted && props.tour?.key === tourKey);

const startTour = (tourKey: string) => {
  emit('start-tour', tourKey);
  close();
};

const loadPage = async () => {
  const requestId = ++pageRequestId;

  if (!open.value || !props.helpKey) {
    page.value = null;
    loading.value = false;
    error.value = props.fallbackReason || null;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const nextPage = await fetchHelpPage(props.helpKey, auth.authHeaders);
    if (requestId !== pageRequestId) {
      return;
    }

    page.value = nextPage;
  } catch (caught) {
    if (requestId !== pageRequestId) {
      return;
    }

    page.value = null;
    error.value = readApiErrorMessage(caught, fallbackMessage);
  } finally {
    if (requestId === pageRequestId) {
      loading.value = false;
    }
  }
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && open.value) {
    close();
  }
};

watch(() => [open.value, props.helpKey], loadPage, { immediate: true });

watch(
  open,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener('keydown', onKeydown);
      return;
    }
    document.removeEventListener('keydown', onKeydown);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Transition name="help-drawer">
    <div
      v-if="open"
      class="help-drawer__overlay"
      role="presentation"
      @click.self="close"
    >
      <aside
        class="help-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-drawer-title"
      >
        <header class="help-drawer__header">
          <div>
            <span class="help-drawer__eyebrow">Помощь по экрану</span>
            <h2 id="help-drawer-title">
              {{ page?.title ?? fallbackTitle }}
            </h2>
          </div>
          <UiIconButton
            aria-label="Закрыть помощь"
            title="Закрыть"
            size="sm"
            variant="ghost"
            @click="close"
          >
            <X :size="18" />
          </UiIconButton>
        </header>

        <div class="help-drawer__body">
          <UiCallout
            v-if="error"
            tone="warning"
          >
            {{ error }}
          </UiCallout>

          <div
            v-else-if="loading"
            class="help-drawer__loading"
          >
            <UiSkeleton
              v-for="skeletonRow in loadingSkeletonRows"
              :key="skeletonRow.key"
              height="1.4rem"
            />
          </div>

          <template v-else-if="page">
            <p class="help-drawer__summary">
              {{ page.summary }}
            </p>

            <UiCard
              v-if="primarySteps"
              class="help-drawer-card help-drawer-card--steps"
              padding="lg"
            >
              <header class="help-drawer-card__header">
                <span
                  class="help-drawer-card__icon"
                  aria-hidden="true"
                >
                  <ClipboardList :size="18" />
                </span>
                <h3>{{ primarySteps.title ?? 'Как работать на экране' }}</h3>
              </header>

              <ol class="help-drawer__steps">
                <li
                  v-for="item in primaryStepItems"
                  :key="item.key"
                >
                  <span>{{ item.order }}</span>
                  <p>{{ item.text }}</p>
                </li>
              </ol>
            </UiCard>

            <UiCard
              v-else-if="primaryList"
              class="help-drawer-card help-drawer-card--steps"
              padding="lg"
            >
              <header class="help-drawer-card__header">
                <span
                  class="help-drawer-card__icon"
                  aria-hidden="true"
                >
                  <ClipboardList :size="18" />
                </span>
                <h3>{{ primaryList.title ?? 'Что важно знать' }}</h3>
              </header>

              <ul class="help-drawer__bullet-list">
                <li
                  v-for="item in primaryListItems"
                  :key="item.key"
                >
                  {{ item.text }}
                </li>
              </ul>
            </UiCard>

            <UiCard
              v-if="keyFields.length"
              class="help-drawer-card"
              padding="lg"
            >
              <header class="help-drawer-card__header">
                <span
                  class="help-drawer-card__icon"
                  aria-hidden="true"
                >
                  <BookMarked :size="18" />
                </span>
                <h3>Ключевые поля</h3>
              </header>

              <dl class="help-drawer__fields">
                <div
                  v-for="field in keyFields"
                  :key="field.key"
                >
                  <dt>{{ field.label }}</dt>
                  <dd>{{ field.hint ?? field.description }}</dd>
                </div>
              </dl>
            </UiCard>

            <div class="help-drawer__actions">
              <UiButton
                :as="RouterLink"
                class="help-drawer__full-link"
                variant="secondary"
                :to="{ name: 'help-page', params: { key: page.key } }"
                :icon="ExternalLink"
                @click="close"
              >
                Открыть полную инструкцию
              </UiButton>
            </div>

            <section
              v-if="visibleTours.length"
              class="help-drawer__tours"
            >
              <UiCard
                class="help-drawer-card"
                padding="lg"
              >
                <header class="help-drawer-card__header">
                  <span
                    class="help-drawer-card__icon help-drawer-card__icon--outline"
                    aria-hidden="true"
                  >
                    <CirclePlay :size="18" />
                  </span>
                  <h3>Туры по экрану</h3>
                </header>

                <ul>
                  <li
                    v-for="tourItem in visibleTours"
                    :key="tourItem.key"
                  >
                    <div>
                      <strong>{{ tourItem.title }}</strong>
                      <span
                        v-if="tourItem.description"
                        class="help-drawer__tour-description"
                      >
                        {{ tourItem.description }}
                      </span>
                    </div>
                    <UiButton
                      type="button"
                      variant="secondary"
                      :icon="isTourCompleted(tourItem.key) ? RotateCcw : CirclePlay"
                      @click="startTour(tourItem.key)"
                    >
                      {{ isTourCompleted(tourItem.key) ? 'Пройти снова' : 'Запустить' }}
                    </UiButton>
                  </li>
                </ul>
              </UiCard>
            </section>
          </template>

          <div
            v-else
            class="help-drawer__empty"
          >
            <p>{{ fallbackMessage }}</p>
            <UiButton
              :as="RouterLink"
              class="help-drawer__full-link"
              variant="secondary"
              :to="{ name: 'help-center' }"
              :icon="ExternalLink"
              @click="close"
            >
              Перейти в справочный центр
            </UiButton>
            <section
              v-if="visibleTours.length"
              class="help-drawer__tours"
            >
              <UiCard
                class="help-drawer-card"
                padding="lg"
              >
                <header class="help-drawer-card__header">
                  <span
                    class="help-drawer-card__icon help-drawer-card__icon--outline"
                    aria-hidden="true"
                  >
                    <CirclePlay :size="18" />
                  </span>
                  <h3>Доступные туры</h3>
                </header>

                <ul>
                  <li
                    v-for="tourItem in visibleTours"
                    :key="tourItem.key"
                  >
                    <div>
                      <strong>{{ tourItem.title }}</strong>
                      <span
                        v-if="tourItem.description"
                        class="help-drawer__tour-description"
                      >
                        {{ tourItem.description }}
                      </span>
                    </div>
                    <UiButton
                      type="button"
                      variant="secondary"
                      :icon="isTourCompleted(tourItem.key) ? RotateCcw : CirclePlay"
                      @click="startTour(tourItem.key)"
                    >
                      {{ isTourCompleted(tourItem.key) ? 'Пройти снова' : 'Запустить' }}
                    </UiButton>
                  </li>
                </ul>
              </UiCard>
            </section>
          </div>
        </div>
      </aside>
    </div>
  </Transition>
</template>

<style scoped>
.help-drawer-enter-active,
.help-drawer-leave-active {
  transition: opacity 0.2s ease;
}

.help-drawer-enter-active .help-drawer,
.help-drawer-leave-active .help-drawer {
  transition: transform 0.24s ease;
}

.help-drawer-enter-from,
.help-drawer-leave-to {
  opacity: 0;
}

.help-drawer-enter-from .help-drawer,
.help-drawer-leave-to .help-drawer {
  transform: translateX(2rem);
}

.help-drawer__overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  justify-content: flex-end;
  background:
    linear-gradient(90deg, rgba(15, 23, 42, 0.18), rgba(15, 23, 42, 0.46)),
    rgba(15, 23, 42, 0.22);
  backdrop-filter: blur(8px);
}

.help-drawer {
  display: grid;
  width: min(41.5rem, calc(100vw - 1rem));
  height: 100%;
  grid-template-rows: auto 1fr;
  overflow: hidden;
  border-left: 1px solid color-mix(in srgb, var(--ui-border) 72%, transparent);
  border-radius: 1.375rem 0 0 1.375rem;
  background:
    radial-gradient(circle at 12% 0%, color-mix(in srgb, var(--ui-accent) 8%, transparent), transparent 34%),
    linear-gradient(180deg, var(--ui-surface) 0%, color-mix(in srgb, var(--ui-surface) 88%, var(--ui-surface-muted)) 100%);
  box-shadow: -2rem 0 5rem rgba(15, 23, 42, 0.22);
  color: var(--ui-foreground);
}

.help-drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ui-space-4);
  margin: 0 2.25rem;
  padding: 2.25rem 0 1.5rem;
  border-bottom: 1px solid var(--ui-border);
}

.help-drawer__header h2 {
  margin: 0.8rem 0 0;
  color: var(--ui-foreground);
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.05em;
}

.help-drawer__eyebrow {
  color: var(--ui-accent);
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.help-drawer__body {
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  padding: 1.75rem 2.25rem 2.5rem;
}

.help-drawer__loading {
  display: grid;
  gap: var(--ui-space-3);
}

.help-drawer__summary {
  max-width: 34rem;
  margin: 0;
  color: var(--ui-foreground-muted);
  font-size: 1.15rem;
  line-height: 1.55;
}

.help-drawer-card {
  --ui-card-shadow: none;

  border-radius: 1.15rem;
  border-color: color-mix(in srgb, var(--ui-accent) 18%, var(--ui-border));
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--ui-surface) 96%, var(--ui-accent) 4%), var(--ui-surface));
}

.help-drawer-card.ui-card--padding-lg {
  padding: 1.5rem;
}

.help-drawer-card__header {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 1.2rem;
}

.help-drawer-card__header h3 {
  margin: 0;
  color: var(--ui-foreground);
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.help-drawer-card__icon {
  display: inline-flex;
  width: 2.05rem;
  height: 2.05rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 0.7rem;
  background: linear-gradient(135deg, var(--ui-accent), color-mix(in srgb, var(--ui-accent) 72%, #7c3aed));
  color: var(--ui-on-accent);
  box-shadow: 0 0.55rem 1.2rem color-mix(in srgb, var(--ui-accent) 22%, transparent);
}

.help-drawer-card__icon :deep(svg) {
  display: block;
}

.help-drawer-card__icon--outline {
  border: 1px solid color-mix(in srgb, var(--ui-accent) 30%, transparent);
  background: color-mix(in srgb, var(--ui-accent) 10%, var(--ui-surface));
  color: var(--ui-accent);
  box-shadow: none;
}

.help-drawer__steps {
  display: grid;
  gap: 0.95rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.help-drawer__steps li {
  display: grid;
  grid-template-columns: 2.05rem 1fr;
  align-items: start;
  gap: 1.1rem;
}

.help-drawer__steps li > span {
  display: inline-flex;
  width: 2.05rem;
  height: 2.05rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: color-mix(in srgb, var(--ui-accent) 14%, var(--ui-surface));
  color: var(--ui-accent);
  font-weight: 800;
}

.help-drawer__steps p,
.help-drawer__bullet-list li {
  margin: 0;
  color: var(--ui-foreground);
  font-size: 1rem;
  line-height: 1.45;
}

.help-drawer__bullet-list {
  display: grid;
  gap: 0.75rem;
  margin: 0;
  padding-left: 1.2rem;
}

.help-drawer__fields {
  margin: 0;
}

.help-drawer__fields div {
  padding: 0 0 1rem;
  border-bottom: 1px solid var(--ui-border);
}

.help-drawer__fields div + div {
  padding-top: 1rem;
}

.help-drawer__fields div:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.help-drawer__fields dt {
  margin: 0 0 0.35rem;
  color: var(--ui-foreground);
  font-size: 1rem;
  font-weight: 800;
}

.help-drawer__fields dd {
  margin: 0;
  color: var(--ui-foreground-muted);
  line-height: 1.45;
}

.help-drawer__actions {
  display: flex;
}

.help-drawer__full-link {
  justify-content: flex-start;
  min-height: 3.1rem;
  padding-inline: 1.25rem;
  border-color: color-mix(in srgb, var(--ui-accent) 25%, var(--ui-border));
  color: var(--ui-accent);
  font-weight: 800;
}

.help-drawer__tours ul {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.help-drawer__tours li {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--ui-border);
}

.help-drawer__tours li:first-child {
  padding-top: 0;
}

.help-drawer__tours li:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.help-drawer__tours strong {
  display: block;
  color: var(--ui-foreground);
  font-weight: 800;
  line-height: 1.3;
}

.help-drawer__tour-description {
  display: block;
  margin-top: 0.35rem;
  color: var(--ui-foreground-muted);
  font-size: 0.9rem;
  line-height: 1.35;
}

.help-drawer__tours :deep(.ui-button) {
  color: var(--ui-accent);
  font-weight: 800;
  white-space: nowrap;
}

.help-drawer__empty {
  display: grid;
  gap: 1rem;
}

.help-drawer__empty p {
  margin: 0;
  color: var(--ui-foreground-muted);
  line-height: 1.5;
}

:global([data-theme='dark']) .help-drawer__overlay {
  background:
    linear-gradient(90deg, rgba(2, 6, 23, 0.24), rgba(2, 6, 23, 0.68)),
    rgba(2, 6, 23, 0.3);
}

:global([data-theme='dark']) .help-drawer {
  border-left-color: color-mix(in srgb, var(--ui-border) 72%, transparent);
  background:
    radial-gradient(circle at 12% 0%, color-mix(in srgb, var(--ui-accent) 12%, transparent), transparent 34%),
    linear-gradient(180deg, color-mix(in srgb, var(--ui-surface) 88%, #0f172a), var(--ui-surface));
}

:global([data-theme='dark']) .help-drawer-card {
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--ui-surface-muted) 72%, var(--ui-accent) 8%), var(--ui-surface));
}

@media (max-width: 768px) {
  .help-drawer {
    width: 100vw;
    max-width: 100vw;
    border-left: 0;
    border-radius: 0;
  }

  .help-drawer__header {
    margin-inline: 1.25rem;
    padding-top: 1.4rem;
  }

  .help-drawer__body {
    padding: 1.25rem;
  }

  .help-drawer__tours li {
    grid-template-columns: 1fr;
  }

  .help-drawer__tours :deep(.ui-button),
  .help-drawer__full-link {
    width: 100%;
  }
}
</style>
